import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Headphones,
    Search,
    Mail,
    Phone,
    Building2,
    Users,
    Stethoscope,
    ClipboardList,
    Target,
    Save,
    X,
    ChevronRight,
    Clock,
    CheckCircle2,
    Archive,
    Circle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_META = {
    nouveau: { label: "Nouveau", cls: "bg-orange-100 text-orange-800 border-orange-200", icon: Circle },
    en_cours: { label: "En cours", cls: "bg-blue-100 text-blue-800 border-blue-200", icon: Clock },
    traite: { label: "Traité", cls: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
    archive: { label: "Archivé", cls: "bg-brand-line/50 text-brand-ink/60 border-brand-line", icon: Archive },
};

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function StatusPill({ status }) {
    const meta = STATUS_META[status] || STATUS_META.nouveau;
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}
            data-testid={`conseiller-status-pill-${status}`}
        >
            <Icon className="h-3 w-3" />
            {meta.label}
        </span>
    );
}

function DetailRow({ icon: Icon, label, children, testid }) {
    return (
        <div className="flex gap-3" data-testid={testid}>
            <Icon className="mt-1 h-4 w-4 shrink-0 text-brand-orange" />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                    {label}
                </div>
                <div className="mt-1 text-sm text-brand-ink">{children}</div>
            </div>
        </div>
    );
}

function DetailList({ items, empty = "—" }) {
    if (!items?.length) return <span className="text-sm text-brand-smoke">{empty}</span>;
    return (
        <ul className="mt-1 space-y-1">
            {items.map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm text-brand-ink/85">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand-orange" />
                    <span>{x}</span>
                </li>
            ))}
        </ul>
    );
}

function DetailDrawer({ item, onClose, onUpdated }) {
    const [status, setStatus] = useState(item?.status || "nouveau");
    const [notes, setNotes] = useState(item?.admin_notes || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setStatus(item?.status || "nouveau");
        setNotes(item?.admin_notes || "");
    }, [item?.id]);

    if (!item) return null;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/conseiller-requests/${item.id}`, {
                status,
                admin_notes: notes,
            });
            toast.success("Demande mise à jour.");
            onUpdated(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-stretch justify-end bg-brand-ink/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="conseiller-detail-overlay"
        >
            <motion.div
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 40, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="h-full w-full max-w-2xl overflow-y-auto bg-white p-8 shadow-2xl"
                data-testid="conseiller-detail-drawer"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <StatusPill status={item.status} />
                            <span className="text-xs text-brand-smoke">
                                #{item.id.slice(0, 8)} · reçue le {formatDate(item.created_at)}
                            </span>
                        </div>
                        <h2
                            className="mt-4 font-display text-3xl leading-tight text-brand-ink"
                            data-testid="conseiller-detail-name"
                        >
                            {item.prenom} {item.nom}
                        </h2>
                        <p className="mt-1 text-sm text-brand-smoke">{item.specialite}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-2 text-brand-smoke transition-colors hover:bg-brand-line/40 hover:text-brand-ink"
                        aria-label="Fermer"
                        data-testid="conseiller-detail-close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Identité */}
                <section className="mt-8 space-y-5 rounded-2xl border border-brand-line bg-[#FAFAFA] p-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        Identité
                    </h3>
                    <DetailRow icon={Mail} label="Email" testid="conseiller-field-email">
                        <a
                            href={`mailto:${item.email}`}
                            className="text-brand-ink underline underline-offset-2 hover:text-brand-orange"
                        >
                            {item.email}
                        </a>
                    </DetailRow>
                    <DetailRow icon={Phone} label="Téléphone" testid="conseiller-field-tel">
                        <a
                            href={`tel:${item.telephone}`}
                            className="text-brand-ink underline underline-offset-2 hover:text-brand-orange"
                        >
                            {item.telephone}
                        </a>
                    </DetailRow>
                    <DetailRow icon={Building2} label="Cabinet" testid="conseiller-field-cabinet">
                        {item.cabinet}
                    </DetailRow>
                </section>

                {/* Cabinet */}
                <section className="mt-6 space-y-5 rounded-2xl border border-brand-line bg-white p-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        Cabinet
                    </h3>
                    <DetailRow icon={Users} label="Nombre de thérapeutes" testid="conseiller-field-nb">
                        {item.nb_therapeutes}
                    </DetailRow>
                    <DetailRow icon={Stethoscope} label="Spécialisations" testid="conseiller-field-spec">
                        <DetailList items={item.specialisations} />
                        {item.specialisations_autres ? (
                            <p className="mt-2 text-sm text-brand-ink/80">
                                <em className="text-brand-smoke">Autres :</em>{" "}
                                {item.specialisations_autres}
                            </p>
                        ) : null}
                    </DetailRow>
                    <DetailRow icon={ClipboardList} label="Équipements" testid="conseiller-field-equip">
                        <DetailList items={item.equipements} />
                        {item.equipements_autres ? (
                            <p className="mt-2 text-sm text-brand-ink/80">
                                <em className="text-brand-smoke">Autres :</em> {item.equipements_autres}
                            </p>
                        ) : null}
                        {item.equipement_prefere ? (
                            <p className="mt-2 text-sm text-brand-ink/80">
                                <em className="text-brand-smoke">Préféré :</em>{" "}
                                {item.equipement_prefere}
                            </p>
                        ) : null}
                    </DetailRow>
                </section>

                {/* Besoins */}
                <section className="mt-6 space-y-5 rounded-2xl border border-brand-line bg-white p-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        Besoins & Objectifs
                    </h3>
                    <DetailRow icon={Stethoscope} label="Pathologies difficiles" testid="conseiller-field-patho">
                        <DetailList items={item.pathologies_difficiles} />
                        {item.pathologies_autres ? (
                            <p className="mt-2 text-sm text-brand-ink/80">
                                <em className="text-brand-smoke">Autres :</em> {item.pathologies_autres}
                            </p>
                        ) : null}
                    </DetailRow>
                    <DetailRow icon={ClipboardList} label="Formations passées" testid="conseiller-field-form">
                        {item.formations_passees == null ? (
                            <span className="text-brand-smoke">Non précisé</span>
                        ) : item.formations_passees ? (
                            <>
                                <span className="font-medium text-brand-ink">Oui</span>
                                {item.formations_passees_details ? (
                                    <p className="mt-1 text-sm text-brand-ink/80">
                                        {item.formations_passees_details}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <span>Non</span>
                        )}
                    </DetailRow>
                    <DetailRow icon={ClipboardList} label="Titre / expertise" testid="conseiller-field-titre">
                        {item.titre_expertise == null ? (
                            <span className="text-brand-smoke">Non précisé</span>
                        ) : item.titre_expertise ? (
                            <>
                                <span className="font-medium text-brand-ink">Oui</span>
                                {item.titre_expertise_details ? (
                                    <p className="mt-1 text-sm text-brand-ink/80">
                                        {item.titre_expertise_details}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <span>Non</span>
                        )}
                    </DetailRow>
                    <DetailRow icon={Target} label="Objectifs formation" testid="conseiller-field-objs">
                        <DetailList items={item.objectifs_formation} />
                    </DetailRow>
                </section>

                {/* Statut + notes */}
                <section className="mt-6 rounded-2xl border border-brand-orange/25 bg-brand-orange/[0.04] p-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        Traitement admin
                    </h3>
                    <div className="mt-4">
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            Statut
                        </label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {Object.keys(STATUS_META).map((s) => {
                                const active = status === s;
                                const meta = STATUS_META[s];
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setStatus(s)}
                                        data-testid={`conseiller-status-${s}`}
                                        className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                                            active
                                                ? "border-brand-orange bg-brand-orange text-white"
                                                : "border-brand-line bg-white text-brand-ink/70 hover:border-brand-ink"
                                        }`}
                                    >
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mt-5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            Notes admin
                        </label>
                        <textarea
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Compte-rendu du rappel, prochaine étape, contexte…"
                            data-testid="conseiller-admin-notes"
                            className="mt-2 w-full resize-none rounded-xl border border-brand-line bg-white p-3 text-sm leading-relaxed outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="conseiller-save-btn"
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d9772c] disabled:opacity-60"
                    >
                        <Save className="h-4 w-4" />
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </section>
            </motion.div>
        </div>
    );
}

export default function AdminConseillerRequests() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        document.title = "Admin · Demandes conseiller";
        reload();
    }, []);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/conseiller-requests");
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const counters = useMemo(() => {
        const c = { total: items.length, nouveau: 0, en_cours: 0, traite: 0, archive: 0 };
        for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return items.filter((it) => {
            if (statusFilter && it.status !== statusFilter) return false;
            if (!s) return true;
            const hay = `${it.prenom} ${it.nom} ${it.email} ${it.cabinet} ${it.specialite}`
                .toLowerCase();
            return hay.includes(s);
        });
    }, [items, statusFilter, search]);

    const onUpdated = (updated) => {
        setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
        setSelected((sel) => (sel && sel.id === updated.id ? { ...sel, ...updated } : sel));
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-conseiller-requests">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                        Funnel commercial
                    </div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">
                        Demandes conseiller
                    </h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Kinés ayant demandé un rappel via <code>/parler-conseiller</code>. Engagement
                        de traitement : sous 48h.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Headphones className="h-7 w-7" />
                </div>
            </div>

            {/* Counters */}
            <div
                className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-5"
                data-testid="conseiller-counters"
            >
                {[
                    { k: "total", label: "Total", value: counters.total, wrap: "border-brand-line bg-white", val: "text-brand-ink" },
                    { k: "nouveau", label: "Nouveaux", value: counters.nouveau, wrap: "border-orange-200 bg-orange-50", val: "text-orange-800" },
                    { k: "en_cours", label: "En cours", value: counters.en_cours, wrap: "border-blue-200 bg-blue-50", val: "text-blue-800" },
                    { k: "traite", label: "Traités", value: counters.traite, wrap: "border-green-200 bg-green-50", val: "text-green-800" },
                    { k: "archive", label: "Archivés", value: counters.archive, wrap: "border-brand-line bg-[#FAFAFA]", val: "text-brand-ink/60" },
                ].map((c) => (
                    <div
                        key={c.k}
                        className={`rounded-2xl border ${c.wrap} p-5`}
                        data-testid={`conseiller-count-${c.k}`}
                    >
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            {c.label}
                        </div>
                        <div className={`mt-2 font-display text-3xl ${c.val}`}>{c.value}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex flex-wrap gap-1 rounded-full border border-brand-line bg-white p-1">
                    {[
                        { v: "", l: "Toutes" },
                        { v: "nouveau", l: "Nouveaux" },
                        { v: "en_cours", l: "En cours" },
                        { v: "traite", l: "Traités" },
                        { v: "archive", l: "Archivés" },
                    ].map((opt) => (
                        <button
                            key={opt.v || "all"}
                            onClick={() => setStatusFilter(opt.v)}
                            data-testid={`conseiller-filter-${opt.v || "all"}`}
                            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                                statusFilter === opt.v
                                    ? "bg-brand-orange text-white"
                                    : "text-brand-smoke hover:text-brand-ink"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="conseiller-search"
                        placeholder="Nom, email, cabinet…"
                        className="w-full rounded-full border border-brand-line bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="py-16 text-center text-sm text-brand-smoke">Chargement…</div>
            ) : filtered.length === 0 ? (
                <div
                    className="mt-8 rounded-2xl border border-dashed border-brand-line bg-white py-16 text-center text-sm text-brand-smoke"
                    data-testid="conseiller-empty"
                >
                    Aucune demande {statusFilter || "à afficher"}.
                </div>
            ) : (
                <div className="mt-6 space-y-3" data-testid="conseiller-list">
                    {filtered.map((it) => (
                        <button
                            key={it.id}
                            onClick={() => setSelected(it)}
                            data-testid={`conseiller-row-${it.id}`}
                            className="group flex w-full items-center gap-4 rounded-2xl border border-brand-line bg-white p-5 text-left shadow-premium transition-all hover:-translate-y-0.5 hover:border-brand-orange/40 hover:shadow-lg"
                        >
                            <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 font-display text-sm text-brand-orange sm:flex">
                                {(it.prenom || "?")[0]}
                                {(it.nom || "?")[0]}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <StatusPill status={it.status} />
                                    <span className="text-[11px] text-brand-smoke">
                                        {formatDate(it.created_at)}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center gap-2 font-display text-lg text-brand-ink">
                                    {it.prenom} {it.nom}
                                    <span className="text-xs font-normal text-brand-smoke">
                                        · {it.specialite}
                                    </span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-brand-smoke">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Mail className="h-3 w-3" /> {it.email}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Phone className="h-3 w-3" /> {it.telephone}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Building2 className="h-3 w-3" /> {it.cabinet}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Users className="h-3 w-3" /> {it.nb_therapeutes} thérapeute
                                        {String(it.nb_therapeutes) !== "1" ? "s" : ""}
                                    </span>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 shrink-0 text-brand-smoke transition-transform group-hover:translate-x-1" />
                        </button>
                    ))}
                </div>
            )}

            <DetailDrawer
                item={selected}
                onClose={() => setSelected(null)}
                onUpdated={onUpdated}
            />
        </div>
    );
}
